import { Check, LoaderCircle, TriangleAlert } from 'lucide-react'
import { PersistenceStatus } from '@/core/persistence'
import { useEditSession } from '@/core/store/editSession'
import { TEST_IDS } from '@/lib/testIds'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/shadcn/tooltip'

const STATUS_LABELS: Readonly<Record<PersistenceStatus, string>> = {
  [PersistenceStatus.Idle]: 'Not saved',
  [PersistenceStatus.Saving]: 'Saving…',
  [PersistenceStatus.Saved]: 'Saved just now',
  [PersistenceStatus.Disabled]: 'Recent edits are not being saved',
}

export function PersistenceStatusIndicator() {
  const status = useEditSession((state) => state.persistenceStatus)
  const label = STATUS_LABELS[status]
  const icon = status === PersistenceStatus.Saved
    ? <Check className="size-3.5" aria-hidden="true" />
    : status === PersistenceStatus.Saving
      ? <LoaderCircle className="size-3.5 animate-spin" aria-hidden="true" />
      : <TriangleAlert className="size-3.5" aria-hidden="true" />

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-testid={TEST_IDS.persistenceStatus}
          data-status={status}
          className={cn(
            'inline-flex items-center gap-1 text-xs text-muted-foreground',
            status === PersistenceStatus.Disabled && 'text-destructive',
          )}
          aria-label={`Persistence: ${label}`}
        >
          {icon}
          <span>{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {status === PersistenceStatus.Disabled
          ? 'Recent edits are not being saved. Check browser storage permissions and space.'
          : label}
      </TooltipContent>
    </Tooltip>
  )
}
